import Form, { Field as BaseField } from "rc-field-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Button from "../../../../../../components/Button";
import Field from "../../../../../../components/Field";
import ResourceSelect from "../../../../../../components/Inputs/ResourceSelect";
import Switch from "../../../../../../components/Inputs/Switch";
import Loading from "../../../../../../components/Loaders/Loading";
import useApi from "../../../../../../utils/api";
import { getErrorMessage } from "../../../../../../utils/helpers/errors";
import {
    getSettingsResourceSearchFiltersWithLookups,
    renderSettingsResourceLabel,
} from "../../../../../../utils/helpers/settings";

const getWorkingShiftSearchFilters = (search) => {
    if (!search) {
        return;
    }
    return getSettingsResourceSearchFiltersWithLookups(search);
};

function WorkingShifts({ user, date, close, refetch }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const { authGet, authPost, authPut, authDelete } = useApi();
    const [loading, setLoading] = useState(false);
    const [userShifts, setUserShifts] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user?.id || !date) return;
        const controller = new AbortController();
        const fetchUserShifts = async () => {
            setLoading(true);
            try {
                const response = await authGet(`/users/${user.id}/working-shift`, {
                    params: { date, user: user.id },
                    signal: controller.signal,
                });
                if (response) {
                    setUserShifts(response?.result || response || []);
                }
            } catch (error) {
                getErrorMessage(error, t);
            } finally {
                setLoading(false);
            }
        };
        fetchUserShifts();
        return () => controller.abort();
    }, [authGet, user?.id, date, t]);

    const nonOverrideShifts = useMemo(() => {
        const record = userShifts.find((item) => !item.override);
        return record?.workingShifts || [];
    }, [userShifts]);

    const overrideRecord = useMemo(() => {
        return userShifts.find((item) => item.override);
    }, [userShifts]);

    useEffect(() => {
        form.setFieldsValue({
            emptyOverride: overrideRecord
                ? !overrideRecord.workingShifts.length
                : false,
            workingShifts: overrideRecord?.workingShifts || [],
        });
    }, [form, overrideRecord]);

    const onSave = useCallback(
        async (data) => {
            setSubmitting(true);
            try {
                const payload = {
                    workingShifts: data.emptyOverride
                        ? []
                        : data.workingShifts?.map((item) => item.id),
                    startEffectiveDate: date,
                    endEffectiveDate: date,
                    user: user.id,
                    override: true,
                };
                const isUpdate = !!overrideRecord;
                const response = isUpdate
                    ? await authPut(`/users/${user.id}/working-shift/${overrideRecord.id}`, { data: payload })
                    : await authPost(`/users/${user.id}/working-shift`, { data: payload });
                if (response) {
                    toast.success(t(isUpdate ? "resource-updated" : "resource-created"));
                    await refetch?.();
                    close?.();
                }
            } catch (error) {
                getErrorMessage(error, t);
            } finally {
                setSubmitting(false);
            }
        },
        [authPost, authPut, overrideRecord, user?.id, date, close, t, refetch],
    );

    const onReset = useCallback(async () => {
        if (!overrideRecord?.id) return;
        setSubmitting(true);
        try {
            await authDelete(`/users/${user.id}/working-shift/${overrideRecord.id}`);
            form.setFieldsValue({ workingShifts: [] });
            toast.success(t("resource-updated"));
            await refetch?.();
            close?.();
        } catch (error) {
            getErrorMessage(error, t);
        } finally {
            setSubmitting(false);
        }
    }, [authDelete, form, overrideRecord?.id, user?.id, close, t, refetch]);

    if (loading) {
        return (
            <div className="py-4 d-flex align-items-center justify-content-center">
                <Loading />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column h-100">
            <div className="mb-4">
                <label className="form-label font-weight-600">
                    {t("user-working-shifts")}
                </label>
                <ResourceSelect
                    resourcePath="/working-shift"
                    renderLabel={renderSettingsResourceLabel}
                    placeholder={t("working-shift")}
                    mode="multiple"
                    value={nonOverrideShifts}
                    disabled
                />
            </div>

            <Form form={form} onFinish={onSave} className="d-flex flex-column flex-grow-1">
                <Field name="emptyOverride" valuePropName="checked">
                    <Switch>
                        {t("override-as-empty")}
                    </Switch>
                </Field>

                <BaseField>
                    {({ }, { }, { getFieldValue }) => {
                        const emptyOverride = getFieldValue("emptyOverride");
                        if (emptyOverride) {
                            return null;
                        }
                        return (
                            <Field
                                name="workingShifts"
                                label={t("override")}
                                rules={[
                                    {
                                        required: true,
                                        message: t("required-working-shift"),
                                    }
                                ]}
                            >
                                <ResourceSelect
                                    resourcePath="/working-shift"
                                    renderLabel={renderSettingsResourceLabel}
                                    getSearchFilters={getWorkingShiftSearchFilters}
                                    placeholder={t("working-shift")}
                                    mode="multiple"
                                    hasSearch
                                />
                            </Field>
                        );
                    }}
                </BaseField>

                <div className="d-flex align-items-center justify-content-end mt-auto py-2">
                    <Button
                        type="button"
                        className="text-dark w-50"
                        onClick={close}
                        disabled={submitting}
                    >
                        {t("cancel")}
                    </Button>

                    <Button
                        type="button"
                        color="danger"
                        onClick={onReset}
                        className="w-50"
                        disabled={!overrideRecord?.id || submitting}
                    >
                        {t("reset")}
                    </Button>

                    <Button
                        type="submit"
                        color="primary"
                        className="px-4 w-100"
                        loading={submitting}
                    >
                        {t("save")}
                    </Button>
                </div>
            </Form>
        </div>
    );
}

export default WorkingShifts;
